import { useState, useRef, useCallback } from 'react';

import diamondIcon from '../assets/diamond.png';
import { createParticleExplosion } from '../utils/ParticleEffect';

export interface FlyingPrize {
  key: string;
  id: string;
  imageUrl: string;
  top: number;
  left: number;
  targetX: number;
  targetY: number;
}

export interface FlyingDiscount {
  key: string;
  id: string;
  imageUrl: string;
  top: number;
  left: number;
  targetX: number;
  targetY: number;
}

// Helper function to animate an element using Web Animations API
const animateElement = (
  element: HTMLElement,
  targetX: number,
  targetY: number,
  duration: number,
  delay: number = 0
): Promise<void> => {
  return new Promise((resolve) => {
    const animation = element.animate(
      [
        {
          transform: 'translate(0, 0) scale(1)',
          opacity: '1',
        },
        {
          transform: `translate(${targetX}px, ${targetY}px) scale(0.5)`,
          opacity: '0',
        },
      ],
      {
        duration,
        delay,
        easing: 'ease-in-out',
        fill: 'forwards',
      }
    );

    animation.addEventListener('finish', () => resolve());
  });
};

// Helper function to animate discount elements
const animateDiscountElement = (
  element: HTMLElement,
  targetX: number,
  targetY: number,
  duration: number,
  delay: number = 0
): Promise<void> => {
  return new Promise((resolve) => {
    const animation = element.animate(
      [
        {
          transform: 'translate(0, 0) scale(1)',
          opacity: '1',
        },
        {
          transform: `translate(${targetX}px, ${targetY}px) scale(0.6)`,
          opacity: '1',
          offset: 0.8,
        },
        {
          transform: `translate(${targetX}px, ${targetY}px) scale(0.6)`,
          opacity: '0',
        },
      ],
      {
        duration,
        delay,
        easing: 'ease-in-out',
        fill: 'forwards',
      }
    );

    animation.addEventListener('finish', () => resolve());
  });
};

export const usePrizeAnimation = () => {
  const [showPrizeAnimation, setShowPrizeAnimation] = useState<boolean>(false);
  const [showDiscountApplied, setShowDiscountApplied] = useState<boolean>(false);
  const [flyingPrizes, setFlyingPrizes] = useState<FlyingPrize[] | null>(null);
  const [flyingDiscounts, setFlyingDiscounts] = useState<FlyingDiscount[] | null>(null);
  const [walletPulse, setWalletPulse] = useState<boolean>(false);
  const [discountPulse, setDiscountPulse] = useState<boolean>(false);
  const walletRef = useRef<HTMLDivElement>(null);

  const triggerPrizeAnimation = useCallback((prizeId: string, imageUrl: string, clickX: number, clickY: number) => {
    // Trigger particle explosion at click position
    createParticleExplosion({
      x: clickX,
      y: clickY,
      count: 25,
      colors: [
        'hsl(45, 100%, 70%)',
        'hsl(50, 100%, 65%)',
        'hsl(40, 100%, 75%)',
        'hsl(55, 100%, 60%)',
        'hsl(35, 100%, 80%)',
        'hsl(42, 100%, 68%)',
      ],
      minSize: 6,
      maxSize: 10,
      distance: 75,
      duration: 2000,
      delay: 150,
    });

    const wallet = walletRef.current;
    const walletRect = wallet?.getBoundingClientRect();

    // Get scroll offset from the routes container
    const routesContainer = document.getElementById('routes-container');
    const scrollTop = routesContainer?.scrollTop || 0;

    // Adjust click coordinates to account for scroll
    const adjustedClickY = clickY + scrollTop - 40;

    // Calculate absolute target position (wallet center)
    let walletCenterX = 20;
    let walletCenterY = window.innerHeight - 30; // Default to bottom of screen
    if (walletRect) {
      walletCenterX = walletRect.left + walletRect.width / 2;
      walletCenterY = walletRect.top + walletRect.height / 2;
    }

    // Calculate translation vector from click to wallet center
    // Wallet is fixed positioned, so use viewport coordinates for target calculation
    const targetX = walletCenterX - clickX;
    const targetY = walletCenterY - clickY - 40;

    // Create flying prizes
    const newFlying = [
      {
        key: `${prizeId}-1-${Date.now()}`,
        id: prizeId,
        imageUrl,
        top: adjustedClickY,
        left: clickX,
        targetX,
        targetY,
      },
      {
        key: `${prizeId}-2-${Date.now()}`,
        id: prizeId,
        imageUrl,
        top: adjustedClickY + 5,
        left: clickX + 5,
        targetX,
        targetY,
      },
      {
        key: `${prizeId}-3-${Date.now()}`,
        id: prizeId,
        imageUrl,
        top: adjustedClickY - 5,
        left: clickX - 5,
        targetX,
        targetY,
      },
    ];

    // Start the animations
    setFlyingPrizes(newFlying);

    // Animate elements once they're rendered
    setTimeout(() => {
      const animationPromises: Promise<void>[] = [];

      newFlying.forEach((prize, index) => {
        const element = document.querySelector(`[data-flying-key="${prize.key}"]`) as HTMLElement;
        if (element) {
          const delay = index * 100; // 100ms stagger
          const duration = index === 1 ? 700 : 800; // Middle one slightly faster
          animationPromises.push(animateElement(element, prize.targetX, prize.targetY, duration, delay));
        }
      });

      // Wait for the last animation to complete, then trigger wallet pulse
      Promise.all(animationPromises).then(() => {
        setWalletPulse(true);
        setTimeout(() => setWalletPulse(false), 300);

        // Show the +3€ animation
        setTimeout(() => {
          setShowPrizeAnimation(true);
          setTimeout(() => setShowPrizeAnimation(false), 1000);
        }, 100);
      });
    }, 16); // Next frame

    // Clear flying prizes after all animations complete
    setTimeout(() => setFlyingPrizes(null), 1000);
  }, []);

  const triggerDiscountAnimation = useCallback(() => {
    setShowDiscountApplied(true);
    setTimeout(() => setShowDiscountApplied(false), 3000);
  }, []);

  const triggerWalletToDiscountAnimation = useCallback((discountElementRef: React.RefObject<HTMLElement>) => {
    const wallet = walletRef.current;
    const discountElement = discountElementRef.current;

    if (!wallet || !discountElement) {
      console.warn('Wallet or discount element not found for animation');
      return;
    }

    const walletRect = wallet.getBoundingClientRect();
    const discountRect = discountElement.getBoundingClientRect();

    // Get scroll offset from the routes container
    const routesContainer = document.getElementById('routes-container');
    const scrollTop = routesContainer?.scrollTop || 0;

    // Calculate start position (wallet center)
    const walletCenterX = walletRect.left + walletRect.width / 2;
    const walletCenterY = walletRect.top + walletRect.height / 2;

    // Calculate target position (discount element center)
    const discountCenterX = discountRect.left + discountRect.width / 2;
    const discountCenterY = discountRect.top + discountRect.height / 2 + scrollTop;

    // Calculate translation vector from wallet to discount
    const targetX = discountCenterX - walletCenterX;
    const targetY = discountCenterY - walletCenterY;

    // Create flying discounts
    const newFlyingDiscounts = [
      {
        key: `discount-1-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: diamondIcon,
        top: walletCenterY,
        left: walletCenterX,
        targetX,
        targetY,
      },
      {
        key: `discount-2-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: diamondIcon,
        top: walletCenterY + 3,
        left: walletCenterX + 3,
        targetX,
        targetY,
      },
      {
        key: `discount-3-${Date.now()}`,
        id: 'wallet-to-discount',
        imageUrl: diamondIcon,
        top: walletCenterY - 3,
        left: walletCenterX - 3,
        targetX,
        targetY,
      },
    ];

    // Start the animations
    setFlyingDiscounts(newFlyingDiscounts);

    // Animate elements once they're rendered
    setTimeout(() => {
      const animationPromises: Promise<void>[] = [];

      newFlyingDiscounts.forEach((discount, index) => {
        const element = document.querySelector(`[data-flying-key="${discount.key}"]`) as HTMLElement;
        if (element) {
          const delay = index * 150; // 150ms stagger
          const duration = index === 1 ? 700 : index === 2 ? 900 : 800;
          animationPromises.push(animateDiscountElement(element, discount.targetX, discount.targetY, duration, delay));
        }
      });

      // Wait for the last animation to complete, then trigger discount pulse
      Promise.all(animationPromises).then(() => {
        setDiscountPulse(true);
        setTimeout(() => setDiscountPulse(false), 400);
      });
    }, 16); // Next frame

    // Clear flying discounts after all animations complete
    setTimeout(() => setFlyingDiscounts(null), 1500);
  }, []);

  return {
    showPrizeAnimation,
    showDiscountApplied,
    flyingPrizes,
    flyingDiscounts,
    walletPulse,
    discountPulse,
    walletRef,
    triggerPrizeAnimation,
    triggerDiscountAnimation,
    triggerWalletToDiscountAnimation,
  };
};
